class CartDrawer {
  constructor() {
    this.drawer = document.getElementById('cart-drawer');
    this.overlay = document.getElementById('cart-drawer-overlay');
    this.itemsList = document.getElementById('cart-items-list');
    this.subtotal = document.getElementById('cart-subtotal');

    this.isUpdating = false;

    if (!this.drawer || !this.overlay || !this.itemsList || !this.subtotal) {
      console.error('Cart drawer elements not found');
      return;
    }

    this.bindEvents();
    this.refreshCart(); // Add initial cart load
  }

  bindEvents() {
    // Handle cart icon click
    document.querySelectorAll('[data-cart-drawer-trigger]').forEach(trigger => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    });

    // Handle add to cart form submissions
    document.querySelectorAll('form[action="/cart/add"]').forEach(form => {
      form.addEventListener('submit', this.handleAddToCart.bind(this));
    });

    // Close drawer when clicking overlay
    this.overlay.addEventListener('click', this.close.bind(this));

    // Handle quantity changes and item removal
    document.addEventListener('click', (event) => {
      const target = event.target.closest('[data-cart-item-increase], [data-cart-item-decrease], [data-cart-item-remove]');
      if (!target) return;

      const cartItem = target.closest('[data-cart-item]');
      if (!cartItem) return;

      if (target.hasAttribute('data-cart-item-increase')) {
        this.updateQuantity(cartItem, 1);
      } else if (target.hasAttribute('data-cart-item-decrease')) {
        this.updateQuantity(cartItem, -1);
      } else if (target.hasAttribute('data-cart-item-remove')) {
        this.removeItem(cartItem);
      }
    });

    // Handle quantity input changes
    document.addEventListener('change', (event) => {
      const target = event.target;
      if (target.hasAttribute('data-cart-item-quantity')) {
        const item = target.closest('[data-cart-item]');
        const newQuantity = parseInt(target.value, 10);
        this.setQuantity(item, newQuantity);
      }
    });
  }

  async handleAddToCart(event) {
    event.preventDefault();
    const form = event.target;

    try {
      const formData = new FormData(form);
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) throw new Error('Network response was not ok');

      const item = await response.json();
      await this.refreshCart();
      this.open();
    } catch (error) {
      console.error('Error:', error);
    }
  }

  async updateQuantity(itemElement, change) {
    if (!itemElement || this.isUpdating) return;

    const currentQuantity = parseInt(itemElement.getAttribute('data-cart-item-quantity'), 10);
    const newQuantity = currentQuantity + change;

    // Add loading state to the item
    this.setItemLoadingState(itemElement, true);

    if (newQuantity > 0) {
      await this.setQuantity(itemElement, newQuantity);
    } else if (newQuantity === 0) {
      await this.removeItem(itemElement);
    }
  }

  async setQuantity(itemElement, quantity) {
    if (!itemElement || this.isUpdating) return;

    this.isUpdating = true;
    const key = itemElement.getAttribute('data-cart-item-key');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Cart update error:', errorData);
        throw new Error('Network response was not ok');
      }

      const cart = await response.json();
      await this.renderCart(cart);
    } catch (error) {
      console.error('Error:', error);
      // Show error state in the cart
      this.itemsList.innerHTML = `
        <li class="py-6">
          <p class="text-center text-red-500">There was an error updating your cart. Please try again.</p>
        </li>
      `;
    } finally {
      this.isUpdating = false;
      this.setItemLoadingState(itemElement, false);
    }
  }

  setItemLoadingState(itemElement, isLoading) {
    if (!itemElement) return;

    const quantityInput = itemElement.querySelector('[data-cart-item-quantity]');
    const increaseBtn = itemElement.querySelector('[data-cart-item-increase]');
    const decreaseBtn = itemElement.querySelector('[data-cart-item-decrease]');
    const removeBtn = itemElement.querySelector('[data-cart-item-remove]');

    if (isLoading) {
      quantityInput?.setAttribute('disabled', 'disabled');
      increaseBtn?.setAttribute('disabled', 'disabled');
      decreaseBtn?.setAttribute('disabled', 'disabled');
      removeBtn?.setAttribute('disabled', 'disabled');

      // Add loading spinner
      if (quantityInput) {
        quantityInput.classList.add('opacity-50');
        const spinner = document.createElement('div');
        spinner.className = 'absolute inset-0 flex items-center justify-center';
        spinner.innerHTML = `
          <svg class="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        `;
        quantityInput.parentElement.style.position = 'relative';
        quantityInput.parentElement.appendChild(spinner);
        quantityInput.dataset.spinner = 'true';
      }
    } else {
      quantityInput?.removeAttribute('disabled');
      increaseBtn?.removeAttribute('disabled');
      decreaseBtn?.removeAttribute('disabled');
      removeBtn?.removeAttribute('disabled');

      // Remove loading spinner
      if (quantityInput && quantityInput.dataset.spinner) {
        quantityInput.classList.remove('opacity-50');
        const spinner = quantityInput.parentElement.querySelector('.absolute');
        if (spinner) {
          spinner.remove();
        }
        delete quantityInput.dataset.spinner;
      }
    }
  }

  async removeItem(itemElement) {
    if (!itemElement) return;

    // Add loading state to the entire cart
    this.itemsList.classList.add('opacity-50');
    this.itemsList.innerHTML = `
      <li class="py-6">
        <div class="flex items-center justify-center">
          <svg class="animate-spin h-6 w-6 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </li>
    `;

    try {
      const key = itemElement.getAttribute('data-cart-item-key');
      const lineIndex = itemElement.getAttribute('data-cart-item-index');

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: parseInt(lineIndex) + 1,
          quantity: 0
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Cart update error:', errorData);
        throw new Error('Network response was not ok');
      }

      await this.refreshCart();
    } catch (error) {
      console.error('Error removing item:', error);
      this.itemsList.innerHTML = `
        <li class="py-6">
          <p class="text-center text-red-500">There was an error removing the item. Please try again.</p>
        </li>
      `;
    } finally {
      // Remove loading state
      this.itemsList.classList.remove('opacity-50');
    }
  }

  async refreshCart() {
    if (this.isUpdating) return;

    this.isUpdating = true;
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Network response was not ok');

      const cart = await response.json();
      await this.renderCart(cart);
    } catch (error) {
      console.error('Error:', error);
      // Show error state in the cart
      this.itemsList.innerHTML = `
        <li class="py-6">
          <p class="text-center text-red-500">There was an error loading your cart. Please try again.</p>
        </li>
      `;
    } finally {
      this.isUpdating = false;
    }
  }

  async renderCart(cart) {
    if (!this.itemsList) return;

    if (cart.item_count === 0) {
      this.itemsList.innerHTML = `
        <li class="py-6">
          <p class="text-center text-gray-500">Your cart is empty</p>
        </li>
      `;
    } else {
      const itemsHtml = cart.items.map((item, index) => `
        <li class="py-6 flex" data-cart-item data-cart-item-key="${item.key}" data-cart-item-url="${item.url}" data-cart-item-title="${item.title}" data-cart-item-index="${index}" data-cart-item-quantity="${item.quantity}">
          <div class="flex-shrink-0 w-24">
            <img src="${item.featured_image ? item.featured_image.url : ''}" alt="${item.featured_image ? item.featured_image.alt : item.title}" class="w-full aspect-[281/376] object-contain">
          </div>

          <div class="ml-4 flex-1 flex flex-col">
            <div>
              <div class="flex flex-col items-start">
                <h3 class="text-xs font-bold uppercase">
                  <a href="${item.url}">${item.title}</a>
                </h3>
                ${item.total_discount && item.total_discount > 0 ?
                    `<span class="uppercase inline-flex items-center text-xs bg-red-50 px-2 py-1 mt-2 text-red-700 tracking-1">Save ${Math.round(
                    (((item.final_price + (item.total_discount / item.quantity)) - item.final_price) / (item.final_price + (item.total_discount / item.quantity))) * 100
                    )}%</span>` : ''
                }
                ${item.total_discount == 0 && item.properties["_compareRetail"] && item.properties["_compareRetail"] != item.final_price ?
                    `<span class="uppercase inline-flex items-center text-xs bg-red-50 px-2 py-1 mt-2 text-red-700 tracking-1">Save ${Math.round(
                    ((item.properties["_compareRetail"] - item.final_price) / item.properties["_compareRetail"]) * 100
                    )}%</span>` : ''
                }
                <p class="mt-2 font-primary text-sm text-gray-500 flex gap-1">
                    ${item.total_discount && item.total_discount > 0 ?
                        `<span class="line-through decoration-1 decoration-gray-300">${this.formatMoney(item.final_price + (item.total_discount / item.quantity))}</span>`
                        : ''
                    }
                    ${item.total_discount == 0 && item.properties["_compareRetail"] && item.properties["_compareRetail"] != item.final_price ?
                        `<span class="line-through decoration-1 decoration-gray-300">${this.formatMoney(item.properties["_compareRetail"])}</span>`
                        : ''
                    }
                    <span class="text-red-600">${this.formatMoney(item.final_price)}</span>
                </p>
              </div>
            </div>
            <div class="flex-1 flex items-end justify-between text-sm mt-2">
              <div class="flex items-center border rounded-md relative">
                <button type="button" class="p-2 text-gray-500 cursor-pointer hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed" data-cart-item-decrease>
                  <span class="sr-only">Decrease quantity</span>
                  <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />
                  </svg>
                </button>
                <input type="number" class="w-12 text-center border-transparent disabled:opacity-50" value="${item.quantity}" min="1" data-cart-item-quantity>
                <button type="button" class="p-2 text-gray-500 cursor-pointer hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed" data-cart-item-increase>
                  <span class="sr-only">Increase quantity</span>
                  <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </button>
              </div>

              <div class="flex">
                <button type="button" class="text-subtitle-3 font-bold text-indigo-600 uppercase cursor-pointer hover:text-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed" data-cart-item-remove>
                  Remove
                </button>
              </div>
            </div>
          </div>
        </li>
      `).join('');

      this.itemsList.innerHTML = itemsHtml;
    }

    if (this.subtotal) {
      this.subtotal.textContent = this.formatMoney(cart.total_price);
    }

    // Update cart count in header if it exists
    const cartCount = document.querySelector('[data-cart-count]');
    if (cartCount) {
      cartCount.textContent = cart.item_count;
    }

    // Update checkout button state
    const checkoutButton = document.querySelector('[data-cart-checkout-button]');
    if (checkoutButton) {
      if (cart.item_count == 0) {
        checkoutButton.classList.add('disabled');
        checkoutButton.setAttribute('disabled', 'disabled');
        checkoutButton.removeAttribute('href');
      } else {
        checkoutButton.classList.remove('disabled');
        checkoutButton.removeAttribute('disabled');
        checkoutButton.setAttribute('href', '/checkout');
      }
    }

    checkoutButton.addEventListener('click', async (e) => {
      e.preventDefault();

      // Show loading state
      checkoutButton.classList.add('opacity-50', 'pointer-events-none');
      const originalText = checkoutButton.innerText;
      checkoutButton.innerText = 'Processing...';

      try {
        // Get current cart
        const res = await fetch('/cart.js');
        const cart = await res.json();

        // Loop through cart items and remove only _compareRetail
        for (let i = 0; i < cart.items.length; i++) {
          const item = cart.items[i];
          const props = item.properties;

          if (props && props["_compareRetail"]) {
            // Create a shallow copy and remove _compareRetail
            const updatedProps = { ...props };
            delete updatedProps["_compareRetail"];

            await fetch('/cart/change.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                line: i + 1, // 1-based index
                quantity: item.quantity,
                properties: updatedProps
              }),
            });
          }
        }

        // Redirect after all updates
        window.location.href = '/checkout';
      } catch (err) {
        console.error('Error cleaning _compareRetail:', err);
        checkoutButton.innerText = 'Error! Try Again';
        checkoutButton.classList.remove('pointer-events-none');
      }
    });
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
  }

  open() {
    if (!this.drawer || !this.overlay) return;
    this.drawer.classList.remove('translate-x-full');
    this.overlay.classList.remove('hidden');
    document.body.style.overflow = 'hidden';
  }

  close() {
    if (!this.drawer || !this.overlay) return;
    this.drawer.classList.add('translate-x-full');
    this.overlay.classList.add('hidden');
    document.body.style.overflow = '';
  }
}

// Initialize cart drawer when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.cartDrawer = new CartDrawer();

  // Intercept fetch calls to update.js
  const originalFetch = window.fetch;
  window.fetch = async function(url, options) {
    const response = await originalFetch(url, options);

    // Clone the response so we can read it multiple times
    const clone = response.clone();

    // Check if this is a call to update.js
    if (url.toString().includes('/cart/update.js')) {
      try {
        const data = await clone.json();
        // Refresh cart drawer with the response data
        window.cartDrawer.refreshCart();
      } catch (error) {
        console.error('Error processing update.js response:', error);
      }
    }

    return response;
  };
});

// Export close function for global use
window.closeCartDrawer = () => {
  if (window.cartDrawer) {
    window.cartDrawer.close();
  }
};

//Bundle fake button handle
/*document.addEventListener('click', function (event) {
  if (event.target.closest('.mbcOverlayOnBtn')) {
    setTimeout(() => {
        window.cartDrawer.refreshCart();
        window.cartDrawer.open();
    }, 1300); // 300ms delay to allow button action to complete
  }
});*/
document.addEventListener('click', function (event) {
  const overlayBtn = event.target.closest('.mbcOverlayOnBtn');
  if (!overlayBtn) return;

  // Find the related "Add to Cart" button in the DOM
  const addToCartBtn = document.querySelector('.add-to-cart');

  if (!addToCartBtn) return;

  // Set up the MutationObserver
  const observer = new MutationObserver((mutationsList) => {
    for (let mutation of mutationsList) {
      if (
        mutation.type === 'childList' &&
        addToCartBtn.textContent.trim() === 'Add to Cart'
      ) {
        // Trigger your functions once the button resets
        window.cartDrawer.refreshCart();
        window.cartDrawer.open();

        // Stop observing
        observer.disconnect();
        break;
      }
    }
  });

  // Start observing changes to the button's content
  observer.observe(addToCartBtn, {
    childList: true,
    subtree: true,
    characterData: true,
  });
});
